import React, { useState } from 'react';
import { useAuth } from '../contexts/global/AuthContext';
import { useTheme } from '../contexts/global/ThemeContext';

// This component demonstrates GLOBAL context usage for authentication
// It uses the global auth state to handle login/register

export function Login() {
  const { login, register, isLoading } = useAuth(); // GLOBAL auth context
  const { theme } = useTheme(); // GLOBAL theme context

  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (isRegister) {
        await register(formData.name, formData.email, formData.password);
      } else {
        await login(formData.email, formData.password);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="login-container">
      <div className="demo-info">
        <h3>Demo Instructions:</h3>
        <ul>
          <li>Use <strong>john@example.com</strong> or <strong>jane@example.com</strong> to login</li>
          <li>Any password works for this demo</li>
          <li>Or register with any email to create a new account</li>
          <li>Notice how auth state is global - affects entire app</li>
        </ul>
      </div>

      <div className="login-card">
        <div className="login-header">
          <h1 className="app-title">📋 Todo Context Demo</h1>
          <p className="app-subtitle">
            Demonstrating Global vs Local Context Patterns
          </p>
          <div className="context-indicators">
            <span className="global-indicator">🌐 Global Auth</span>
            <span className="global-indicator">🎨 Global Theme</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h2>{isRegister ? 'Create Account' : 'Sign In'}</h2>

          {error && <div className="error">{error}</div>}

          {isRegister && (
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required={isRegister}
                className="form-input"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder={isRegister ? "your@email.com" : "john@example.com or jane@example.com"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Any password works for demo"
            />
          </div>

          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="form-footer">
          <button
            type="button"
            onClick={() => setIsRegister(!isRegister)}
            className="toggle-mode-btn"
          >
            {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
          </button>
        </div>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3rem;
          background: ${theme === 'dark' ? 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
          padding: 1rem;
        }

        .login-card {
          background: ${theme === 'dark' ? '#2d3748' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#2d3748'};
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          padding: 2rem;
          width: 100%;
          max-width: 400px;
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .app-title {
          font-size: 2rem;
          font-weight: bold;
          margin: 0 0 0.5rem 0;
        }

        .app-subtitle {
          color: ${theme === 'dark' ? '#a0aec0' : '#6b7280'};
          margin: 0 0 1rem 0;
        }

        .context-indicators {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .global-indicator {
          background: #059669;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
        }

        .login-form {
          margin-bottom: 1rem;
        }

        .login-form h2 {
          text-align: center;
          margin: 0 0 1.5rem 0;
          font-size: 1.5rem;
        }

        .error {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.75rem;
          border-radius: 0.375rem;
          margin-bottom: 1rem;
          text-align: center;
          font-size: 0.875rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: ${theme === 'dark' ? '#e2e8f0' : '#374151'};
        }

        .form-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid ${theme === 'dark' ? '#4a5568' : '#d1d5db'};
          border-radius: 0.375rem;
          background: ${theme === 'dark' ? '#374151' : '#ffffff'};
          color: ${theme === 'dark' ? '#ffffff' : '#374151'};
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 0.75rem;
          border-radius: 0.375rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-footer {
          text-align: center;
          padding-top: 1rem;
          border-top: 1px solid ${theme === 'dark' ? '#4a5568' : '#e5e7eb'};
        }

        .toggle-mode-btn {
          background: none;
          border: none;
          color: #3b82f6;
          cursor: pointer;
          text-decoration: underline;
          font-size: 0.875rem;
        }

        .toggle-mode-btn:hover {
          color: #2563eb;
        }

        .demo-info {
          margin-top: 1.5rem;
          padding: 1rem;
          background: ${theme === 'dark' ? '#1a202c' : '#f8fafc'};
          border-radius: 0.5rem;
          font-size: 0.875rem;
        }

        .demo-info h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .demo-info ul {
          margin: 0;
          padding-left: 1.5rem;
        }

        .demo-info li {
          margin: 0.25rem 0;
        }

        .demo-info strong {
          color: #3b82f6;
        }
      `}</style>
    </div>
  );
}
